import { useEffect, useState } from 'react';
import { useAnimatedStyle, useSharedValue, withTiming, runOnJS } from 'react-native-reanimated';
import Datadog from '../../_shared/datadog';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { UserImageProps } from '@photosi/rn-components/user-image/UserImage';
import { IUserImageModel } from '../../store/_models/userImage';

export const useUserImageLogic = ({
  userImage,
  showOverflow = false,
  disableSwapImageAnimation = false,
}: UserImageProps) => {

  const [currentImage, setCurrentImage] = useState<IUserImageModel>(userImage);
  const [nextImage, setNextImage] = useState<IUserImageModel | undefined>(undefined);

  // Dual SharedValues for crossfade transition
  const currentImageOpacitySV = useSharedValue(1); // Current image starts visible
  const nextImageOpacitySV = useSharedValue(0); // Next image starts hidden

  // Improved crossfade completion handler with rendering synchronization
  const handleCrossfadeCompletion = (newUserImage: IUserImageModel) => {
    // Step 1: Set BOTH currentImage and nextImage to the new userImage value
    setCurrentImage(newUserImage);
    setNextImage(newUserImage);

    // Step 2: Use requestAnimationFrame to wait for the new currentImage to complete rendering
    requestAnimationFrame(() => {
      // Step 3: Only after rendering is complete, safely reset nextImage to null and opacity values
      setNextImage(undefined);
      currentImageOpacitySV.value = 1;
      nextImageOpacitySV.value = 0;
    });
  };

  useEffect(() => {
    if (disableSwapImageAnimation) {
      // For disabled animation case, also use the improved sequence to prevent flashes
      handleCrossfadeCompletion(userImage);
      return;
    }
    if (userImage.id !== currentImage.id) {
      // Set the next image before starting crossfade animation
      setNextImage(userImage);

      // Start crossfade: fade out current, fade in next simultaneously
      currentImageOpacitySV.value = withTiming(0, { duration: 500 });
      nextImageOpacitySV.value = withTiming(1, { duration: 500 }, finished => {
        if (finished) {
          // Use runOnJS to call the improved completion handler
          runOnJS(handleCrossfadeCompletion)(userImage);
        }
      });
    }
  }, [userImage.id, currentImage.id, currentImageOpacitySV, nextImageOpacitySV]);

  useEffect(() => {
    // End Datadog measure for filter apply time
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.filter_apply_time);
  }, [userImage.url]);

  useEffect(() => {
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.photo_edit_save_time);
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time);
  }, []);

  // Animated styles for current image (fade-out during transition)
  const currentImageAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: currentImageOpacitySV.value,
    };
  }, [currentImageOpacitySV]);

  // Animated styles for next image (fade-in during transition)
  const nextImageAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: nextImageOpacitySV.value,
    };
  }, [nextImageOpacitySV]);

  return {
    userImage,
    showOverflow,
    currentImageAnimatedStyle,
    nextImageAnimatedStyle,
    currentImage,
    nextImage,
  };
};
